// @file Dash make button store
import { trackEvent } from '@@/bits/analytics'
import { WallAccessRole, WallPermission } from '@@/enums'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useDashboardSettingsStore } from '@@/pinia/dashboard_settings'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import { useDashCollectionsStore } from '@@/pinia/dash_collections_store'
import { useDashMakeStore } from '@@/pinia/dash_make_store'
import { useDashWallSingleActionsStore } from '@@/pinia/dash_wall_single_actions_store'
import { useSettingsSchoolPermissionsStudentsStore } from '@@/pinia/settings_school_permissions_students'
import { useSettingsSchoolPermissionsTeachersStore } from '@@/pinia/settings_school_permissions_teachers'
import { UserGroupWallCollaboratorApi } from '@@/surface/api/user_group_wall_collaborator'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const useDashMakeButtonStore = defineStore('dashMakeButtonStore', () => {
  const dashAccountsStore = useDashAccountsStore()
  const dashMakeStore = useDashMakeStore()
  const settingsSchoolPermissionsStudentsStore = useSettingsSchoolPermissionsStudentsStore()
  const settingsSchoolPermissionsTeachersStore = useSettingsSchoolPermissionsTeachersStore()
  const dashboardSettingsStore = useDashboardSettingsStore()
  const dashCollectionsStore = useDashCollectionsStore()
  const dashWallSingleActionsStore = useDashWallSingleActionsStore()
  const contentPickerStore = useContentPickerStore()
  // ------------------------------
  // Navigating from a bookmark collection
  // ------------------------------
  const recentlyClickedBookmarkName = ref<string | null>(null)
  const recentlyClickedBookmarkId = ref<number | null>(null)
  const isBookmarkNavigation = computed<boolean>(() => recentlyClickedBookmarkName.value != null)

  const xDashMakeButton = computed<boolean>(
    () =>
      (dashAccountsStore.currentLibrary == null || !dashMakeStore.cannotMakeWall) &&
      !['trashed', 'combined_shared', 'archived'].includes(
        dashCollectionsStore.activeCollectionKey.indexKey as string,
      ) &&
      dashCollectionsStore.isActiveSearchQueryEmpty &&
      contentPickerStore.activeContentPickerPanel == null,
  )

  // ------------------------------
  // Navigating from a user group collection
  // ------------------------------
  const recentlyClickedUserGroupId = ref<number | null>(null)
  const isUserGroupNavigation = computed<boolean>(() => recentlyClickedUserGroupId.value != null)

  // Returns the default wall visitor permission for the current user, this differs based on the student role
  const currentUserDefaultSchoolLibraryWallVisitorPermission = computed<WallAccessRole>(() => {
    if (!dashboardSettingsStore.isSchoolLibrary) {
      return WallAccessRole.Writer // Changed from WallPermission.Write
    }

    const defaultWallPermission = dashAccountsStore.isStudent
      ? (settingsSchoolPermissionsStudentsStore.permissionsState.defaultWallVisitorPermission as WallPermission)
      : (settingsSchoolPermissionsTeachersStore.permissionsState.defaultWallVisitorPermission as WallPermission)

    switch (defaultWallPermission) {
      case WallPermission.Write:
        return WallAccessRole.Writer
      case WallPermission.Read:
        return WallAccessRole.Reader
      default:
        return WallAccessRole.Writer
    }
  })

  // ------------------------------
  // Miscellaneous
  // ------------------------------

  const userCameFromDashMakeButtonClick = ref<boolean>(false)

  // Handle post-wall creation actions like adding bookmarks or inviting collaborators
  // This function is called any place where we create a wall from the dash make button
  async function handleDashMakeWallCreationActions(wallId: number): Promise<void> {
    if (isBookmarkNavigation.value) {
      const folderId = dashCollectionsStore.activeFolderId ?? recentlyClickedBookmarkId.value
      await dashWallSingleActionsStore.addBookmark({
        folderId,
        wallId,
        throwException: true,
      })
    } else if (isUserGroupNavigation.value && recentlyClickedUserGroupId.value != null) {
      await UserGroupWallCollaboratorApi.invite({
        wallId,
        userGroupId: recentlyClickedUserGroupId.value,
        role: currentUserDefaultSchoolLibraryWallVisitorPermission.value,
      })
    }
    // Track the padlet creation if the user came from the dash make button
    if (userCameFromDashMakeButtonClick.value) {
      trackEvent('Dashboard', 'Created a padlet from dash make button')
    }
  }

  return {
    // ------------------------------
    // Navigating from a bookmark collection
    // ------------------------------
    recentlyClickedBookmarkName,
    recentlyClickedBookmarkId,
    recentlyClickedUserGroupId,
    xDashMakeButton,

    // ------------------------------
    // Navigating from a user group collection
    // ------------------------------
    isBookmarkNavigation,
    isUserGroupNavigation,
    currentUserDefaultSchoolLibraryWallVisitorPermission,

    // ------------------------------
    // Miscellaneous
    // ------------------------------
    handleDashMakeWallCreationActions,
    userCameFromDashMakeButtonClick,
  }
})
export { useDashMakeButtonStore }
